<script setup lang="ts">
import FieldsList from '@components/FieldsList/FieldsList.vue'
import { ref, useAttrs, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { IModalFormOptions } from './types'
import { prepareParentFields } from './utils'

import { SectionsClass } from '@/api/fields'
import { showError, showSuccess } from '@/notifications'
import { useFieldsStore } from '@/stores/fieldsStore'
import { usePreloaderStore } from '@/stores/preloader.store'
import { request } from '@/utils/request'
import { transformPath } from '@/utils/transformer'

const GET_FIELDS_URL = '/api/form/fields'
const SET_FIELDS_URL = '/api/form/set'

const attrs = useAttrs()
const dialogRef = defineModel<boolean>({ required: true })

const preloaderStore = usePreloaderStore()

const { options, params, selectedUuids, scheduleUuid, recordUuid, parentUuid = '' } = defineProps<{
  options: IModalFormOptions,
  params?: any,
  selectedUuids?: string[],
  scheduleUuid?: string,
  recordUuid?: string,
  parentUuid?: string
}>()

interface IFieldsParams {
  ButtonTitle: string
  FormName: string
  SubTitle: string
  Title: string
  Uuid: string
}

const route = useRoute()
const router = useRouter()

const emit = defineEmits(['close', 'submit'])
const sectionsRef = ref<SectionsClass | null>()
const fieldsParams = ref<IFieldsParams>()
let fieldsRequestId = 0

if (Array.isArray(options.ParentFields) && options.ParentFields?.length) {
  // Получаем поля родителя (стор мог заполнить только он)
  const fieldsStore = useFieldsStore()
  const parentFields = fieldsStore.getFields

  prepareParentFields(parentFields, options.ParentFields, fieldsStore.getEntityType, route, parentUuid)
}

const loadParams: Record<string, any> = {}

// Обработаем случай с 'loadFieldsParams'
// Необходимо при добавлении урока https://uc.garapas.uchi.red/directory/course/97d659bf-c39e-4e00-b5b6-b63d1d33271e?tab=lesson
if (Array.isArray(options.ParentFields) && options.ParentFields?.length) {
  options.ParentFields.forEach(field => {
    if (field.FieldUseType === 'loadFieldsParams') {
      if (field.ParentFieldName === 'Uuid') {
        loadParams[field.Name] = parentUuid || route.params.uuid
      } else {
        loadParams[field.Name] = field.Value
      }
    }
  })
}

const setValuesFromParentFields = (sections: SectionsClass) => {
  const setFieldValue = (field, value) => {
    field.value = field.type === 'list' || field.type === 'listInt'
      ? field.prepareValue(value)
      : value
  }

  if (Array.isArray(options.ParentFields) && options.ParentFields.length) {
    options.ParentFields.forEach((field) => {
      if (field.FieldUseType === 'field') {
        const parentField = sections.getFieldByName(field.Name)

        if (parentField) {
          setFieldValue(parentField, field.Value)

          if (parentField.type === 'Widget') {
            parentField._values = field.Value
          }
        }
      }

      // TODO_VUE переработать функцию
      sections.sections.forEach((section) => {
        section.fields.forEach((sectionField) => {
          if (sectionField.name === field.Name) {
            if (field.FieldUseType === 'fieldViewConfig') {
              sectionField.options.viewFilterField[field.ViewFilterFieldName] = Array.isArray(field.Value) ? (field.Value[0]?.Bundle?.Uuid || field.Value[0]?.Uuid) : field.Value
            } else {
              setFieldValue(sectionField, field.Value)

              if (field.Name === 'Uuid') {
                sectionField.type = 'string'
                sectionField.visible = false
              }
            }
          }
        })
      })
    })
  }
}

// В опциях могут быть значения для предустановки в поля
// при открытии формы (gorn-754)
const setValuesFromOptions = (sections: SectionsClass) => {
  for (const [key, value] of Object.entries(options)) {
    if (typeof value !== 'string' && typeof value !== 'boolean' && typeof value !== 'number') continue

    const field = sections.getFieldByName(key)
    if (!field) continue

    if (field.multipleValues) {
      field.value = field.type === 'list' || field.type === 'listInt'
        ? field.prepareValue([value])
        : [value]
    } else {
      field.value = field.prepareValue(value)
    }
  }
}

const setFieldsParams = (params: Record<string, any>) => {
  fieldsParams.value = {
    ButtonTitle: params.ButtonTitle || '',
    FormName: params.FormName || '',
    SubTitle: params.SubTitle || '',
    Title: params.Title || '',
    Uuid: params.Uuid || ''
  }
}

const getFields = async () => {
  const requestId = ++fieldsRequestId
  const data = {
    __id: options.FormUuid || options.AdapterUuid,
    __params: Object.assign({}, {
      ...(loadParams || {}),
      ...(params || {})
    })
  }

  try {
    preloaderStore.show()

    const response = await request(GET_FIELDS_URL, {
      method: 'POST',
      body: JSON.stringify(data)
    })

    // За время запроса форму могли закрыть или открыть заново.
    // Не восстанавливаем уже неактуальные значения полей.
    if (requestId !== fieldsRequestId || !dialogRef.value) return

    sectionsRef.value = new SectionsClass({
      sections: response.sections,
      sectionsParams: response.sectionsParams,
      fields: response.fields,
      params: response.params,
      recordUuid
    })

    setFieldsParams(response.params || {})

    setValuesFromParentFields(sectionsRef.value)
    setValuesFromOptions(sectionsRef.value)
  } catch (e) {
    console.error(e)
  } finally {
    preloaderStore.hide()
  }
}

const onSave = async (sections: SectionsClass | null | undefined) => {
  if (!sections) return

  if (!sections.checkValues()) {
    showError('Проверьте правильность заполнения полей.')
    return
  }

  const data = sections.getValues()
  data.append('__id', options.FormUuid || options.AdapterUuid || '')
  data.append('AdapterUuid', options.FormUuid || options.AdapterUuid || '')

  if (recordUuid) {
    data.append('__recordUuid', recordUuid)
  }
  // Временный костыль
  if (scheduleUuid) {
    data.append('ScheduleUuid', scheduleUuid)
  }
  if (selectedUuids && selectedUuids.length) {
    selectedUuids.forEach((uuid, index) => {
      data.append(`UuidsList__${index}`, uuid)
    })
    data.delete('UuidsList')
  }

  try {
    preloaderStore.show()
    const needPrint = options.FormType === 'print'

    const response = await request(SET_FIELDS_URL, {
      method: 'POST',
      body: data
    }, false, needPrint
      ? {
          onDownload: (url) => {
            const downloadUrl = url.startsWith('/') ? url : `/${url}`
            window.open(downloadUrl, '_blank')
          }
        }
      : {})

    showSuccess('Успешно')
    emit('submit')
    closeDialog()
    
    // Если получили ссылку для редиректа, адаптируем ссылку и перенаправим пользователя
    if (response.goto) {
      const target = router.resolve(transformPath(response.goto))
      const query = { ...target.query }

      delete query.reload
      query.tab = 'preview'

      await router.push({
        path: target.path,
        query
      }) 
    }

    return response
  } catch (e) {
    console.error(e)
  } finally {
    preloaderStore.hide()
  }
}

const closeDialog = () => {
  dialogRef.value = false
}

watch(dialogRef, (isOpen, wasOpen) => {
  if (isOpen) {
    getFields()
    return
  }

  fieldsRequestId++
  sectionsRef.value = null
  fieldsParams.value = undefined

  if (wasOpen) emit('close')
}, { immediate: true })
</script>

<template>
  <VDialog
    v-model="dialogRef"
    v-bind="attrs"
    :retain-focus="false"
  >
    <VCard class="d-flex flex-column">
      <VToolbar>
        <VToolbarTitle>
          {{ options.FormTitle || fieldsParams?.Title || 'Универсальная форма' }}
          <VSpacer />
        </VToolbarTitle>
        <VToolbarItems>
          <VBtn @click="closeDialog">
            <VIcon
              icon="mdi-close"
              size="large"
            />
          </VBtn>
        </VToolbarItems>
      </VToolbar>

      <VCardText
        v-if="sectionsRef"
        class="flex-grow-1 overflow-y-auto"
      >
        <FieldsList
          v-model="sectionsRef"
          :config="{__uuid: options.FormUuid || fieldsParams?.Uuid }"
        />
      </VCardText>

      <VCardActions>
        <VBtn
          variant="elevated"
          color="primary"
          :disabled="preloaderStore.isLoading"
          @click="() => onSave(sectionsRef)"
        >
          {{ options.FormButtonTitle || fieldsParams?.ButtonTitle || 'Добавить' }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
